import { findVehicleStart } from "Store/findVehicle/slice";
import { buttonTrackId } from "Utils/analytics";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { CarProductIntroFormValues, VehicleInputType } from "../interfaces";
import { AlertThemedButton, StyledLicensePlateObjectFinder } from "./styled";

import { Icons } from "@storebrand-digital/insurance-definitions";
import { AccessibleCallUsMessage } from "Components/Form/AccessibleCallUsMessage";
import { AlertWithIllustration } from "Components/Form/Alert";
import { useTexts } from "Components/TextContext";
import { Button as CommonButtonText } from "Components/TextContext/commonComponentEnums";
import { removeWhitespace } from "Formatting/index";
import { ReduxState } from "Store/rootReducer";
import { useUserIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import { useField, useFormikContext } from "formik";
import { resetVehicle, setVehicle } from "../utils";
import { FoundCarElement } from "./FoundCarElement";

export const FindVehicle = (): JSX.Element => {
  const formProps = useFormikContext<CarProductIntroFormValues>();
  const { getText } = useTexts();
  const [licensePlateNumberFieldInputProps] = useField<string>(
    "licensePlateNumberSearch",
  );

  const dispatch = useDispatch();
  const userIdentification = useUserIdentificationSelector();

  const customerRef = userIdentification?.newCustomerResponse.customerRef;

  const searchHandler = (): void => {
    if (!isDefined(formProps.errors.licensePlateNumberSearch)) {
      dispatch(
        findVehicleStart({
          licensePlateNumber: removeWhitespace(
            String(formProps.values.licensePlateNumberSearch),
          ),
          customerRef: customerRef ?? "",
          onSuccess: (foundVehicle) => {
            formProps.setFieldValue(
              "licensePlateNumberSearch",
              foundVehicle.regNumber,
            );
            setVehicle(formProps, foundVehicle);
            setTimeout(() => {
              formProps.submitForm();
            }, 0);
          },
        }),
      );
    }
  };

  const {
    current: vehicle,
    error,
    isLoading,
  } = useSelector((state: ReduxState) => state.findVehicle);

  if (isDefined(vehicle)) {
    return <FoundCarElement />;
  }

  return (
    <>
      <StyledLicensePlateObjectFinder
        searchProps={{
          ...licensePlateNumberFieldInputProps,
          id: licensePlateNumberFieldInputProps.name,
          fluid: true,
          large: true,
          label: getText("carform.label.licenceplatenumbersearch"),
          error: getError(licensePlateNumberFieldInputProps.name, formProps),
          button: {
            buttonText: getText(CommonButtonText.SEARCH),
            onSearch: searchHandler,
          },
        }}
        isLoading={isLoading}
      />

      {isDefined(error) && (
        <AlertWithIllustration
          alertId="no-search-results"
          iconName={Icons.CAR}
          title={getText("carform.alerttitle.noregistrationnumberfound")}
          description={getText(
            "carform.alertdescription.providecarinfoforfurtherhelp",
          )}
          marginBottom="24px"
        >
          {formProps.values.vehicleInputType !== VehicleInputType.MANUAL && (
            <>
              <AccessibleCallUsMessage />
              <AlertThemedButton
                id="vehicle-input-type-alert"
                trackId={buttonTrackId("vehicle-input-type-alert")}
                type="button"
                variant="text"
                iconRight="arrow-right"
                onClick={() => {
                  formProps.setFieldValue(
                    "vehicleInputType",
                    VehicleInputType.MANUAL,
                  );
                  resetVehicle(formProps);
                }}
              >
                {getText("carform.button.providecarinfo")}
              </AlertThemedButton>
            </>
          )}
        </AlertWithIllustration>
      )}
    </>
  );
};
